// src/App.js
import React, { useState, useEffect, useCallback } from 'react';
import Dashboard from './components/Dashboard';
import { EXCLUDED_DATES, THRESHOLDS, CUTOFF_DATES } from './utils/calculations';

const API_BASE = 'https://api.cemon.ru/otcheti';
const SURVEY_API_BASE = 'https://apiopros2024.paragraf.spb.ru';

function App() {
  const [allData, setAllData] = useState({
    dates: [],
    errors: [],
    publication: [],
    compliance: [],
    timeliness: [],
    survey: {
      city: null,
      districts: {},
      schools: {}
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    district: '',
    school: ''
  });

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError(null);

    const { month, year } = filters;
    const id = 36;

    const now = new Date();
    const selectedDate = new Date(year, month - 1, 1);
    const isFutureMonth = selectedDate > now;

    if (isFutureMonth) {
      setAllData(prev => ({
        ...prev,
        dates: [],
        errors: [],
        publication: [],
        compliance: [],
        timeliness: []
      }));
      setLoading(false);
      return;
    }

    try {
      const [datesData, errorsData, publicationData, complianceData, timelinessData] = await Promise.all([
        fetchData(`${API_BASE}/dati_tm_kp_rk/?id=${id}&m=${month}&g=${year}`),
        fetchData(`${API_BASE}/menuoshib/?id=${id}&m=${month}&g=${year}`),
        fetchData(`${API_BASE}/menupub/?id=${id}&m=${month}&g=${year}`),
        fetchData(`${API_BASE}/sootvmenu/?id=${id}&m=${month}&g=${year}`),
        fetchData(`${API_BASE}/svorvrem/?id=${id}&m=${month}&g=${year}`)
      ]);

      const filteredDates = (datesData || []).filter(school => {
        const noPrimaryKeywords = ['нет начальной школы', 'без начальной школы', 'нет нач. кл.'];
        const name = school.Наименование.toLowerCase();
        return !noPrimaryKeywords.some(keyword => name.includes(keyword));
      });

      setAllData({
        dates: filteredDates,
        errors: errorsData || [],
        publication: publicationData || [],
        compliance: complianceData || [],
        timeliness: timelinessData || [],
        survey: {
          city: null,
          districts: {},
          schools: {}
        }
      });
    } catch (err) {
      console.error('Ошибка загрузки данных:', err);
      setError('Ошибка загрузки данных. Попробуйте обновить страницу.');
    } finally {
      setLoading(false);
    }
  }, [filters.month, filters.year]);

  const loadSurveyData = useCallback(async (level, id, name, schoolIds = []) => {
    try {
      let surveyData;
      
      switch (level) {
        case 'city':
          surveyData = await loadCitySurveyData(schoolIds);
          break;
        case 'district':
          surveyData = await loadDistrictSurveyData(id, name, schoolIds);
          break;
        case 'school':
          surveyData = await loadSchoolSurveyData(id, name, schoolIds);
          break;
        default:
          return null;
      }

      setAllData(prev => {
        const newSurvey = { ...prev.survey };
        switch (level) {
          case 'city': newSurvey.city = surveyData; break;
          case 'district': newSurvey.districts[id] = surveyData; break;
          case 'school': newSurvey.schools[id] = surveyData; break;
        }
        return { ...prev, survey: newSurvey };
      });

      return surveyData;
    } catch (error) {
      console.error(`Ошибка загрузки опросов для ${level}:`, error);
      return null;
    }
  }, []);

  const loadCitySurveyData = async (schoolIds) => {
    if (!schoolIds || schoolIds.length === 0) return { parents: [], children: [] };
    const answerValues = schoolIds.slice(0, 1000);
    const [parentsResponse, childrenResponse] = await Promise.all([
      fetchSurveyData('/query', { answer_values: answerValues }),
      fetchSurveyData('/query-new', { answer_values: answerValues })
    ]);
    return {
      parents: parentsResponse.success ? parentsResponse.data : [],
      children: childrenResponse.success ? childrenResponse.data : []
    };
  };

  const loadDistrictSurveyData = async (districtId, districtName, schoolIds) => {
    if (!schoolIds || schoolIds.length === 0) return { parents: [], children: [] };
    const answerValues = schoolIds.slice(0, 50);
    const [parentsResponse, childrenResponse] = await Promise.all([
      fetchSurveyData('/query', { answer_values: answerValues }),
      fetchSurveyData('/query-new', { answer_values: answerValues })
    ]);
    return {
      parents: parentsResponse.success ? parentsResponse.data : [],
      children: childrenResponse.success ? childrenResponse.data : []
    };
  };

  const loadSchoolSurveyData = async (schoolId, schoolName, schoolIds) => {
    const answerValues = schoolIds && schoolIds.length > 0 ? schoolIds.slice(0, 10) : [schoolId];
    const [parentsResponse, childrenResponse] = await Promise.all([
      fetchSurveyData('/query', { answer_values: answerValues }),
      fetchSurveyData('/query-new', { answer_values: answerValues })
    ]);
    return {
      parents: parentsResponse.success ? parentsResponse.data : [],
      children: childrenResponse.success ? childrenResponse.data : []
    };
  };

  const fetchSurveyData = async (endpoint, params = {}) => {
    try {
      const response = await fetch(`${SURVEY_API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error(`Ошибка загрузки опросов с ${endpoint}:`, error);
      return { success: false, data: [] };
    }
  };

  const fetchData = async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error(`Ошибка загрузки данных с ${url}:`, error);
      return [];
    }
  };

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const updateFilters = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    scrollToTop();
  };

  useEffect(() => {
    if (!loading && allData.dates.length > 0) {
      scrollToTop();
    }
  }, [loading, allData.dates, scrollToTop]);

  if (loading && allData.dates.length === 0) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <div>Загрузка данных...</div>
      </div>
    );
  }

  return (
    <div className="app">
      <Dashboard
        allData={allData}
        filters={filters}
        updateFilters={updateFilters}
        loading={loading}
        error={error}
        onReload={loadAllData}
        onLoadSurveyData={loadSurveyData}
      />
    </div>
  );
}

export default App;